// App shell: routes, top bar on desktop, tab bar on phones, and the Help sheet.

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { api } from './lib/api';
import { linkClick, match, navigate, useLocation } from './lib/router';
import { Button, Host, Modal, toast, toastError } from './lib/ui';
import { Icon, type IconName } from './lib/icons';
import { Login } from './pages/Login';
import { Welcome } from './pages/Welcome';
import { Emails } from './pages/Emails';
import { Report } from './pages/Report';
import { Contacts } from './pages/Contacts';
import { Import } from './pages/Import';
import { Settings } from './pages/Settings';
import { Sequences } from './pages/Sequences';
import { Help } from './pages/Help';
import { Year } from './pages/Year';
import { Editor } from './editor/Editor';
import { SendSteps } from './send/SendSteps';

const NAV: { href: string; label: string; icon: IconName; match: (p: string) => boolean }[] = [
  { href: '/', label: 'Emails', icon: 'mail', match: (p) => p === '/' || p.startsWith('/emails') || p === '/year' },
  { href: '/contacts', label: 'Contacts', icon: 'users', match: (p) => p.startsWith('/contacts') },
  { href: '/automatic', label: 'Automatic', icon: 'repeat', match: (p) => p.startsWith('/automatic') },
  { href: '/settings', label: 'Settings', icon: 'settings', match: (p) => p.startsWith('/settings') },
];

export function App() {
  const { path } = useLocation();
  const bare = path === '/login' || path === '/welcome';
  const [ready, setReady] = useState(bare);

  useEffect(() => {
    if (bare) return;
    api('/api/me')
      .then(() => setReady(true))
      .catch(() => {});
  }, [bare]);

  let page: ComponentChildren = null;
  let full = false; // full-screen pages draw their own top bar
  let m: Record<string, string> | null;
  if (path === '/login') page = <Login />;
  else if (path === '/welcome') page = <Welcome />;
  else if (!ready) page = null;
  else if (path === '/') page = <Emails />;
  else if ((m = match('/emails/:id/edit', path))) {
    page = <Editor key={m.id} id={Number(m.id)} />;
    full = true;
  } else if ((m = match('/emails/:id/send', path))) {
    page = <SendSteps key={m.id} id={Number(m.id)} />;
    full = true;
  } else if ((m = match('/emails/:id', path))) page = <Report key={m.id} id={Number(m.id)} />;
  else if (path === '/contacts') page = <Contacts />;
  else if (path === '/contacts/import') page = <Import />;
  else if (path === '/automatic') page = <Sequences />;
  else if (path === '/settings') page = <Settings />;
  else if (path === '/help') page = <Help />;
  else if (path === '/year') page = <Year />;
  else page = <NotFound />;

  return (
    <>
      {bare || full ? page : <Shell path={path}>{page}</Shell>}
      <Host />
    </>
  );
}

function Shell({ path, children }: { path: string; children: ComponentChildren }) {
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <div class="shell">
      <header class="topbar">
        <a href="/" class="brand" onClick={(e) => linkClick(e, '/')}>
          <img src="/brand/logo.png" alt="LaVir" />
          <span>Email</span>
        </a>
        <nav class="topnav" aria-label="Main">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} class={n.match(path) ? 'on' : ''} aria-current={n.match(path) ? 'page' : undefined} onClick={(e) => linkClick(e, n.href)}>
              {n.label}
            </a>
          ))}
        </nav>
        <div class="topbar-right">
          <button class="help-link" title="Help, or tell us something" aria-label="Help" onClick={() => setHelpOpen(true)}>
            <Icon name="help" size={18} />
            <span>Help</span>
          </button>
          <Button kind="primary" icon="plus" class="hide-phone" onClick={() => navigate('/?new=1')}>
            New email
          </Button>
        </div>
      </header>
      <main class={`page page-${path.split('/')[1] || 'home'}`}>{children}</main>
      <nav class="tabbar" aria-label="Main">
        {NAV.slice(0, 2).map((n) => (
          <TabLink key={n.href} n={n} path={path} />
        ))}
        <button class="tab-new" aria-label="New email" onClick={() => navigate('/?new=1')}>
          <Icon name="plus" size={24} stroke={2.2} />
        </button>
        {NAV.slice(2).map((n) => (
          <TabLink key={n.href} n={n} path={path} />
        ))}
      </nav>
      {helpOpen && <ReportProblem onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function TabLink({ n, path }: { n: (typeof NAV)[number]; path: string }) {
  const on = n.match(path);
  return (
    <a href={n.href} class={on ? 'on' : ''} aria-current={on ? 'page' : undefined} onClick={(e) => linkClick(e, n.href)}>
      <Icon name={n.icon} size={22} />
      <span>{n.label}</span>
    </a>
  );
}

export type NoteKind = 'problem' | 'idea';

/** One sheet for "something's wrong" and "I have an idea". Both are saved as tickets for support. */
export function ReportProblem({ onClose, prefill = '', kind: start = 'problem' }: { onClose: () => void; prefill?: string; kind?: NoteKind }) {
  const [kind, setKind] = useState<NoteKind>(start);
  const [text, setText] = useState(prefill);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const idea = kind === 'idea';
  const empty = !text.trim();
  // Opened from the header, the sheet is the way into the Help page. Opened on the Help page, it is just "Send a note".
  const onHelp = location.pathname === '/help';
  const send = async () => {
    // Send looks grey until there are words. A tap on it then puts her in the box instead of doing nothing.
    if (empty) {
      box.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await api('/api/tickets', {
        body: {
          kind,
          title: text.trim().split('\n')[0].slice(0, 120),
          detail: text.trim(),
          context: { page: location.pathname + location.search, screen: `${innerWidth}x${innerHeight}`, browser: navigator.userAgent },
        },
      });
      toast(idea ? 'Thanks. We read every idea.' : "Sent. We're on it.");
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={onHelp ? 'Send a note' : 'Help'}
      onClose={onClose}
      footer={
        <>
          <Button kind="primary" icon="send" busy={busy} class={empty ? 'is-waiting' : ''} aria-disabled={empty || undefined} onClick={send}>
            Send
          </Button>
        </>
      }
    >
      {!onHelp && (
        <a
          href="/help"
          class="sheet-help-row"
          onClick={(e) => {
            onClose();
            linkClick(e, '/help');
          }}
        >
          <Icon name="help" size={18} />
          <span>Help and past notes</span>
          <Icon name="chevron" size={16} />
        </a>
      )}
      <div class="note-kinds" role="radiogroup" aria-label="Kind of note">
        <button type="button" role="radio" aria-checked={!idea} class={`note-kind ${!idea ? 'on' : ''}`} onClick={() => setKind('problem')}>
          <Icon name="alert" size={20} />
          <span>Something's wrong</span>
        </button>
        <button type="button" role="radio" aria-checked={idea} class={`note-kind idea ${idea ? 'on' : ''}`} onClick={() => setKind('idea')}>
          <Icon name="bulb" size={20} />
          <span>I have an idea</span>
        </button>
      </div>
      <textarea
        ref={box}
        class="note-text"
        rows={4}
        aria-label={idea ? 'Your idea' : 'What happened'}
        value={text}
        onInput={(e) => setText(e.currentTarget.value)}
        placeholder={idea ? 'I wish I could...' : 'What happened? A sentence is plenty.'}
      />
    </Modal>
  );
}

function NotFound() {
  return (
    <div class="narrow center-page">
      <h1 class="display">This page doesn't exist</h1>
      <p class="lead">The link may be old.</p>
      <Button kind="primary" onClick={() => navigate('/')}>
        Go to your emails
      </Button>
    </div>
  );
}
